




























































import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';

import mixins from '@/utils/mixins';
import FormRules from '@/mixins/file-upload-rules';

import ErrorAlert from '../common/ErrorAlert.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import TimeOffHolidayCalendarPicker from './TimeOffHolidayCalendarPicker.vue';
import Messages from '../common/Messages.vue';
import { IFetchCalendarHolidaysParams } from '@/services/timeoff-calendar';
import { ALForm } from '@/common/component.types';

const baseMixins = mixins(FormRules);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'UploadCalendarHoliday',

    components: {
      DragDropFileInput,
      ErrorAlert,
      TimeOffHolidayCalendarPicker,
      Messages
    },

    props: {
      errorMessage: {
        type: String,
        default: ''
      },

      errorMessageDetail: {
        type: String,
        default: ''
      },

      formValue: {
        type: Boolean,
        default: false
      }
    },

    data: () => ({
      loading: false,

      file_upload: null as File | null,

      lazyErrorMessage: '',
      lazyErrorMessageDetail: '',

      calendarSearch: new Date().getFullYear().toString(),
      calendar: null as null | number,
      year: new Date().getFullYear().toString(),

      requiredRules: [(v: string) => !!v || 'This field is required']
    }),

    computed: {
      internalErrorMessage: {
        get(): string {
          if (this.lazyErrorMessage) return this.lazyErrorMessage;
          return this.errorMessage;
        },
        set(value: string) {
          this.$emit('update:errorMessage', value);
          this.lazyErrorMessage = value;
        }
      },

      internalErrorMessageDetail: {
        get(): string {
          if (this.lazyErrorMessageDetail) return this.lazyErrorMessageDetail;
          return this.errorMessageDetail;
        },
        set(value: string) {
          this.$emit('update:errorMessageDetail', value);
          this.lazyErrorMessageDetail = value;
        }
      },

      calendarHolidayUploadFormValid: {
        get(): boolean {
          return this.formValue;
        },
        set(value: boolean) {
          this.$emit('update:formValue', value);
        }
      }
    },

    methods: {
      ...mapActions({
        fetchTimeOffCalendars: 'timeOffCalendar/fetchTimeOffCalendars',
        uploadCalendarHolidays: 'timeOffCalendar/uploadCalendarHolidays'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      clearAllErrors(): void {
        this.internalErrorMessage = '';
        this.internalErrorMessageDetail = '';
        this.$emit('clearAllErrors');
      },

      async uploadHoliday(): Promise<any> {
        if (this.validateForm()) {
          this.loading = true;

          this.clearAllErrors();
          return this.uploadCalendarHolidays({
            calendarId: this.calendar,
            year: this.year,
            file: this.file_upload
          })
            .then(() => {
              const params: IFetchCalendarHolidaysParams = {
                year: this.year,
                calendarId: this.calendar!
              };
              this.$emit('refresh', params);
              this.close();
            })
            .catch(error => {
              if (error.response) {
                // client received an error response that falls out of range 2xx
                if (error.response.data.detail) {
                  this.internalErrorMessageDetail = error.response.data.detail;
                }
              }

              this.internalErrorMessage = ErrorManager.extractApiError(error);
              this.$emit('failed', error);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      },

      resetFormData() {
        this.file_upload = null;
        this.year = new Date().getFullYear().toString();
        this.calendar = null;
        this.resetFormValidation();
      },

      close() {
        this.clearAllErrors();
        this.resetFormData();
        this.$emit('close');
      }
    },

    async mounted() {
      await this.fetchTimeOffCalendars().catch(error => {
        if (error.response) {
          if (error.response.data.detail) {
            this.internalErrorMessageDetail = error.response.data.detail;
          }
        }

        this.internalErrorMessage = ErrorManager.extractApiError(error);
      });
    }
  });
